/**
 * views/Kitchen/kitchenUi.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Shared tokens and formatters for every kitchen surface.
 *
 * ## Why the kitchen has its own palette
 *
 * The rest of the dashboard is a light admin theme (components/dashboardTheme.js)
 * read at a desk. A kitchen board is read from three metres away, over a hot
 * pass, often on a wall-mounted screen with glare, by someone whose hands are
 * full. That is a different design problem: dark ground so the tiles are the
 * only bright thing, high-chroma status colours that survive a cheap panel, and
 * type big enough to read while walking past.
 *
 * ## The colour system, and why it is built this way
 *
 * Colour does exactly two jobs here and they are kept apart:
 *
 *   STATUS  (how bad is it)   four steps, the only colours that must be told
 *                             apart from each other at a glance
 *   STAGE   (where is it)     one hue at descending lightness, a sequence, never
 *                             a set of competing hues
 *
 * The status steps were chosen by running the four-step ramp through the
 * palette validator against this exact surface (#0f1729), not by eye:
 *
 *   CVD separation      PASS   worst adjacent pair dE 13.0 (deutan)
 *   Normal-vision floor PASS   worst adjacent pair dE 18.4
 *   Contrast vs surface PASS   all four clear 3:1
 *
 * The earlier palette did not. Its "running close" amber and "late" orange
 * measured dE 0.1 under deuteranopia, which is to say a colourblind operator
 * could not tell a kitchen running close from a kitchen already late, and even
 * with full colour vision the pair sat at 4.1, far below the readable floor of
 * 15. Five differently-tinted numerals in a row is also what made the board look
 * noisy. Do not add a fifth status hue: there is no room left in the space.
 *
 * Stage colours and the late-versus-breached pair are deliberately sequential
 * within one hue, so their step-to-step separation is low BY DESIGN. They never
 * carry meaning alone: every stage is a titled column and every severity is a
 * written label. Position and words carry identity, colour only reinforces it.
 *
 * ## Where colour is allowed to touch text
 *
 * On the operations board, numbers wear ink and a small coloured pip beside the
 * label carries the state. Tinting every figure is what turned a five-number
 * summary into a traffic accident.
 *
 * The one exception is the age timer on a vendor ticket. At three metres that
 * number IS the status, the tile's own left edge already repeats it, and the
 * label sits beside it. A rule that improves a desk report should not make a
 * wall screen worse.
 */

export const KDS = {
  // ── Surfaces. Two, not five. Everything sits on `bgPanel`; `bgTile` is only
  //    for a genuinely raised element, and `bg` is the ground behind the panel.
  bg: "#0b1220",
  bgPanel: "#0f1729",
  bgTile: "#131d33",
  bgTileHover: "#17223c",
  bgLane: "#0d1526",

  // ── Rules, not boxes. A hairline divides; a border encloses. Prefer dividing.
  border: "#1e2a44",
  borderBright: "#2b3b5c",

  text: "#eef2f9",
  textMid: "#9fb0cc",
  textDim: "#68789a",

  // ── STAGE. One hue, descending lightness, with a green payoff at Ready and a
  //    muted terminal. A sequence, not a set of categories.
  new: "#9cc2ff",
  accepted: "#7dabf8",
  preparing: "#5f8fdd",
  ready: "#5ed39b",
  handedOff: "#55688c",

  // ── STATUS. The validated four. These four, and only these four, have to be
  //    independently identifiable.
  onTime: "#5ed39b",
  atRisk: "#f0b429",
  late: "#f2777a",
  breached: "#e0524f",

  // ── One action colour for the whole surface. A primary button that changed
  //    hue with the ticket's stage made every board look like five products.
  action: "#7dabf8",
  actionInk: "#06101f",

  danger: "#f2777a",
  dangerSoft: "#2a1620",
  success: "#5ed39b",
  info: "#7dabf8",

  radius: "10px",
  radiusSm: "8px",
  shadow: "0 10px 34px rgba(0, 0, 0, 0.34)",
};

export const STATUS_META = {
  NEW: { label: "New", colour: KDS.new, action: "Accept" },
  ACKNOWLEDGED: { label: "Accepted", colour: KDS.accepted, action: "Start cooking" },
  IN_PREP: { label: "Preparing", colour: KDS.preparing, action: "Food is up" },
  READY: { label: "Ready", colour: KDS.ready, action: "Hand over" },
  HANDED_OFF: { label: "Handed off", colour: KDS.handedOff, action: null },
  REJECTED: { label: "Rejected", colour: KDS.danger, action: null },
  CANCELLED: { label: "Cancelled", colour: KDS.textDim, action: null },
};

export const SLA_META = {
  ON_TIME: { label: "On time", colour: KDS.onTime },
  AT_RISK: { label: "Running close", colour: KDS.atRisk },
  LATE: { label: "Late", colour: KDS.late },
  BREACHED: { label: "Breached", colour: KDS.breached },
};

export const ITEM_STATUS_META = {
  PENDING: { label: "To do", colour: KDS.textMid },
  IN_PREP: { label: "Cooking", colour: KDS.preparing },
  DONE: { label: "Done", colour: KDS.ready },
  VOIDED: { label: "Voided", colour: KDS.danger },
};

/**
 * The single most important signal on the board: how long has this been
 * waiting, relative to what we promised. Returns the tile's edge colour.
 *
 * Falls back to absolute thresholds when a ticket has no promise attached, so
 * an unconfigured kitchen still gets an ageing cue rather than a flat wall of
 * identical tiles.
 */
export function ageColour(ticket) {
  const state = ticket?.sla?.state;
  if (state && SLA_META[state]) return SLA_META[state].colour;

  const minutes = (ticket?.ageMs || 0) / 60000;
  if (minutes > 30) return KDS.breached;
  if (minutes > 20) return KDS.late;
  if (minutes > 12) return KDS.atRisk;
  return KDS.onTime;
}

/**
 * Nothing is tinted when there is nothing to report.
 *
 * A colour that always appears stops meaning anything, so a zero count keeps
 * its label and drops its pip. This is what turns an idle board from a wall of
 * lit indicators into a quiet one.
 */
export const pipFor = (count, colour) => (Number(count) > 0 ? colour : null);

/** "8m 12s" / "1h 04m" / "42s". Never a bare millisecond count. */
export function formatDuration(ms, { compact = false } = {}) {
  if (ms === null || ms === undefined || !Number.isFinite(Number(ms))) return "--";
  const total = Math.max(0, Math.round(Number(ms) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return compact ? `${m}m` : `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

/** Minutes, one decimal, for report tables where seconds are noise. */
export function formatMinutes(ms) {
  if (ms === null || ms === undefined || !Number.isFinite(Number(ms))) return "--";
  return `${(Number(ms) / 60000).toFixed(1)} min`;
}

export function formatClock(value) {
  if (!value) return "--";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "--";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function formatPercent(value, digits = 1) {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "--";
  return `${(Number(value) * 100).toFixed(digits)}%`;
}

/**
 * Figures.
 *
 * `tabular-nums` gives every digit the width of a zero, which is what makes a
 * column of numbers align and what makes a large standalone number look loose.
 * So: tabular in table columns, proportional on a headline value. Two tokens
 * rather than one habit, because the habit is always applied to the wrong one.
 */
export const NUM_TABULAR = { fontVariantNumeric: "tabular-nums" };
export const NUM_DISPLAY = { fontVariantNumeric: "proportional-nums", letterSpacing: "-0.02em" };

/** Micro-label: the small uppercase caption above a value or a table column. */
export const MICRO_LABEL = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.085em",
  textTransform: "uppercase",
  color: KDS.textDim,
};

/** Options carry the difference between a right order and a remake. */
export function describeOptions(item) {
  const parts = [];
  const push = (source) => {
    if (!source || typeof source !== "object") return;
    Object.entries(source).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") return;
      if (Array.isArray(value)) {
        const names = value
          .map((v) => (typeof v === "object" ? v?.name || v?.label || v?.value : v))
          .filter(Boolean);
        if (names.length) parts.push(`${key}: ${names.join(", ")}`);
        return;
      }
      if (typeof value === "object") {
        const name = value?.name || value?.label || value?.value;
        if (name) parts.push(`${key}: ${name}`);
        return;
      }
      parts.push(`${key}: ${value}`);
    });
  };
  push(item?.options);
  push(item?.selectedSpecs);
  return parts;
}

/**
 * A stable colour per station code, so the same station looks the same
 * everywhere. Stations are identity, not status, so they draw from their own
 * set and never from the four reserved status steps.
 */
const STATION_PALETTE = [
  "#e8894f", "#d7b13f", "#6fbf73", "#5aa9d6",
  "#a98cd8", "#d97fae", "#4fb3a5", "#c2705f",
];

export function stationColour(code, stations = []) {
  const configured = stations.find(
    (s) => String(s.code).toUpperCase() === String(code).toUpperCase()
  );
  if (configured?.colour) return configured.colour;
  const key = String(code || "MAIN");
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return STATION_PALETTE[hash % STATION_PALETTE.length];
}

/** Money without a library. The dashboard has no shared formatter for this. */
export function formatMoney(amount, currency) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "--";
  const code = currency || "UGX";
  return `${code} ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export const KITCHEN_REJECT_REASONS = [
  "Out of stock",
  "Kitchen at capacity",
  "Closing time",
  "Equipment down",
  "Customer requested cancellation",
  "Duplicate order",
];

export const KITCHEN_VOID_REASONS = [
  "Out of stock",
  "Spoiled or dropped",
  "Wrong item cooked",
  "Customer changed the order",
  "Quality below standard",
];

export default KDS;
